import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { ReviewsState } from "./reviews.state";
import { ReviewsSummary, Review, ReviewDetail } from "./reviews.types";
import {
  getReviewsSummary,
  getReviews,
  addReview,
  editReview,
  removeReview,
  getReviewById,
  forceRemoveReview,
} from "./reviews.thunk";

const initialState: ReviewsState = {
  summary: {
    data: null,
    status: "idle",
    error: null,
  },
  reviews: {
    data: [],
    status: "idle",
    error: null,
  },
  currentReview: {
    data: null,
    status: "idle",
    error: null,
  },
  createStatus: "idle",
  updateStatus: "idle",
  deleteStatus: "idle",
  error: null,
};

export const reviewsSlice = createSlice({
  name: "reviews",
  initialState,
  reducers: {
    resetReviews: (state) => {
      state.summary = initialState.summary;
      state.reviews = initialState.reviews;
      state.currentReview = initialState.currentReview;
      state.createStatus = "idle";
      state.updateStatus = "idle";
      state.deleteStatus = "idle";
      state.error = null;
    },
    resetReviewsList: (state) => {
      state.reviews.data = [];
      state.reviews.status = "idle";
      state.reviews.error = null;
    },
    resetCurrentReview: (state) => {
      state.currentReview = initialState.currentReview;
    },
    resetCreateStatus: (state) => {
      state.createStatus = "idle";
      state.error = null;
    },
    resetUpdateStatus: (state) => {
      state.updateStatus = "idle";
      state.error = null;
    },
    resetDeleteStatus: (state) => {
      state.deleteStatus = "idle";
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getReviewsSummary.pending, (state) => {
        state.summary.status = "loading";
        state.summary.error = null;
      })
      .addCase(
        getReviewsSummary.fulfilled,
        (state, action: PayloadAction<ReviewsSummary>) => {
          state.summary.status = "success";
          state.summary.data = action.payload;
        },
      )
      .addCase(
        getReviewsSummary.rejected,
        (state, action: PayloadAction<string | undefined>) => {
          state.summary.status = "error";
          state.summary.error = action.payload ?? "Özet yüklenemedi.";
        },
      )

      .addCase(getReviews.pending, (state) => {
        state.reviews.status = "loading";
        state.reviews.error = null;
      })
      .addCase(
        getReviews.fulfilled,
        (state, action: PayloadAction<Review[]>) => {
          state.reviews.status = "success";
          state.reviews.data = action.payload;
        },
      )
      .addCase(
        getReviews.rejected,
        (state, action: PayloadAction<string | undefined>) => {
          state.reviews.status = "error";
          state.reviews.error =
            action.payload ?? "Değerlendirmeler yüklenemedi.";
        },
      )

      .addCase(addReview.pending, (state) => {
        state.createStatus = "loading";
        state.error = null;
      })
      .addCase(addReview.fulfilled, (state) => {
        state.createStatus = "success";
        state.error = null;
      })
      .addCase(
        addReview.rejected,
        (state, action: PayloadAction<string | undefined>) => {
          state.createStatus = "error";
          state.error = action.payload ?? "Değerlendirme kaydedilemedi.";
        },
      )

      .addCase(editReview.pending, (state) => {
        state.updateStatus = "loading";
        state.error = null;
      })
      .addCase(
        editReview.fulfilled,
        (state, action: PayloadAction<ReviewDetail>) => {
          state.updateStatus = "success";
          state.error = null;
          const index = state.reviews.data.findIndex(
            (review) => review.id === action.payload.id,
          );
          if (index !== -1) {
            state.reviews.data[index] = {
              ...state.reviews.data[index],
              rating: action.payload.rating,
            };
          }
        },
      )
      .addCase(
        editReview.rejected,
        (state, action: PayloadAction<string | undefined>) => {
          state.updateStatus = "error";
          state.error = action.payload ?? "Değerlendirme güncellenemedi.";
        },
      )

      .addCase(removeReview.pending, (state) => {
        state.deleteStatus = "loading";
        state.error = null;
      })
      .addCase(
        removeReview.fulfilled,
        (state, action: PayloadAction<number>) => {
          state.deleteStatus = "success";
          state.error = null;
          state.reviews.data = state.reviews.data.filter(
            (review) => review.id !== action.payload,
          );
        },
      )
      .addCase(
        removeReview.rejected,
        (state, action: PayloadAction<string | undefined>) => {
          state.deleteStatus = "error";
          state.error = action.payload ?? "Değerlendirme silinemedi.";
        },
      )

      // Get Review by ID
      .addCase(getReviewById.pending, (state) => {
        state.currentReview.status = "loading";
        state.currentReview.error = null;
      })
      .addCase(
        getReviewById.fulfilled,
        (state, action: PayloadAction<ReviewDetail>) => {
          state.currentReview.status = "success";
          state.currentReview.data = action.payload;
        },
      )
      .addCase(
        getReviewById.rejected,
        (state, action: PayloadAction<string | undefined>) => {
          state.currentReview.status = "error";
          state.currentReview.error =
            action.payload ?? "Değerlendirme yüklenemedi.";
        },
      )

      // Force Delete Review
      .addCase(forceRemoveReview.pending, (state) => {
        state.deleteStatus = "loading";
        state.error = null;
      })
      .addCase(
        forceRemoveReview.fulfilled,
        (state, action: PayloadAction<number>) => {
          state.deleteStatus = "success";
          state.error = null;
          state.reviews.data = state.reviews.data.filter(
            (review) => review.id !== action.payload,
          );
        },
      )
      .addCase(
        forceRemoveReview.rejected,
        (state, action: PayloadAction<string | undefined>) => {
          state.deleteStatus = "error";
          state.error =
            action.payload ?? "Değerlendirme kalıcı olarak silinemedi.";
        },
      );
  },
});

export const {
  resetReviews,
  resetReviewsList,
  resetCurrentReview,
  resetCreateStatus,
  resetUpdateStatus,
  resetDeleteStatus,
} = reviewsSlice.actions;

export default reviewsSlice.reducer;
